/** @format */

import {useLayoutEffect, type ReactNode} from 'react';

import {Box, GlobalStyles} from '@mui/material';
import {CssVarsProvider, styled, useColorScheme} from '@mui/material/styles';

import {theme} from './theme';
import {DS_SCOPE_CLASS} from './theme/scope';
import {SurfaceContextProvider, type SurfaceContext} from './theme/surfaceContext';

export {DS_SCOPE_CLASS};

/**
 * Компоненты DS рассчитаны на border-box (в Storybook его даёт CssBaseline).
 * Scoped, а не глобально: старое приложение верстали при content-box.
 */
const scopeBaseline = (
  <GlobalStyles
    styles={{
      [`.${DS_SCOPE_CLASS}`]: {boxSizing: 'border-box'},
      // Раздельно, а не одним списком селекторов: jsdom на псевдоэлементе
      // внутри списка отбрасывает всё правило и тест проходит вхолостую.
      [`.${DS_SCOPE_CLASS} *`]: {boxSizing: 'border-box'},
      // `.ads-scope::before` — не то же, что `.ads-scope *::before`: у порталов
      // класс висит на корне, и его собственные псевдоэлементы иначе не попадут.
      [`.${DS_SCOPE_CLASS}::before, .${DS_SCOPE_CLASS}::after`]: {
        boxSizing: 'border-box',
      },
      [`.${DS_SCOPE_CLASS} *::before, .${DS_SCOPE_CLASS} *::after`]: {
        boxSizing: 'border-box',
      },
    }}
  />
);

/** `display: contents` — чтобы обёртка не разорвала цепочку `height: 100%`. */
const Scope = styled(Box)({display: 'contents'});

export interface DesignSystemProviderProps {
  children: ReactNode;
  /** Surface-тон острова. По умолчанию 'dark' (база). 'light' — для светлых поверхностей. */
  mode?: SurfaceContext;
}

/**
 * Держит РЕЗОЛВНУТУЮ цветовую схему острова тёмной, не давая MUI свалиться в
 * OS-preference. Рендерится внутри CssVarsProvider (нужен его контекст).
 *
 * Почему это нужно и почему `defaultColorScheme`/`colorSchemeNode`/`defaultMode`
 * недостаточно: когда в теме определены ОБЕ схемы ('dark' и 'light'), MUI
 * CssVarsProvider вычисляет `defaultMode='system'`, а проп `defaultMode` в этой
 * версии игнорируется (его нет в списке пропов компонента — проверено). System
 * → на машине с prefers-color-scheme:light MUI резолвит `colorScheme='light'`,
 * и это влияет на ДВЕ вещи:
 *   1. JS-объект `theme.palette` мержится СВЕТЛОЙ палитрой — любое ЛИТЕРАЛЬНОЕ
 *      чтение `theme.palette.*` (surface, text, …) запекает светлый hex в
 *      emotion-CSS (var(--ads-*) здесь не участвует).
 *   2. `data-color-scheme` на `<html>` (var-каскад).
 * Отключить только (2) через `colorSchemeNode={null}` НЕ помогает — литеральные
 * чтения всё равно светлые. Поэтому императивно фиксируем режим `setMode('dark')`
 * в `useLayoutEffect` (до paint → без вспышки; после первого монтирования режим
 * ещё и персистится в `modeStorageKey`, так что и первый рендер уже тёмный).
 */
function PinDarkMode() {
  const {mode, setMode} = useColorScheme();
  useLayoutEffect(() => {
    if (mode !== 'dark') setMode('dark');
  }, [mode, setMode]);
  return null;
}

/**
 * Островной провайдер дизайн-системы.
 *
 * - `CssVarsProvider` даёт DS-тему как React-контекст ТОЛЬКО этому поддереву
 *   и инжектит `--ads-*` в `:root` (emotion дедуплицирует между островами) —
 *   так порталы (Modal/Snackbar/Menu) тоже видят переменные.
 * - `PinDarkMode` держит резолвнутую схему тёмной (см. его JSDoc) — иначе на
 *   light-OS остров рендерился бы светлой палитрой и в literal-, и в var-
 *   чтениях. При dark-режиме MUI ставит `<html data-color-scheme="dark">`,
 *   поэтому и inline-, и портал-контент тёмные (без layout-обёртки на корне).
 * - Side-effects: НЕ рендерим `CssBaseline`/`InitColorSchemeScript` — он
 *   переписал бы документ целиком, а с ним и старые экраны; вместо него
 *   `scopeBaseline` на поддерево. `modeStorageKey` изолирует mode-стейт.
 * - Светлые поверхности (Modal и т.п.) включают `data-color-scheme="light"` на
 *   своей под-обёртке через `SurfaceContextProvider`; var-токены (`surfaceVar`)
 *   в этом поддереве флипаются в светлые (литеральные чтения остаются тёмной
 *   базой — так и задумано, см. surfaceContext).
 *
 * Правило острова: внутри — только DS-компоненты и голый DOM. Не оборачивать
 * сюда старые компоненты (они читают app-тему).
 */
export function DesignSystemProvider({children, mode = 'dark'}: DesignSystemProviderProps) {
  return (
    <CssVarsProvider theme={theme} modeStorageKey='ads-color-mode' disableTransitionOnChange>
      <PinDarkMode />
      {scopeBaseline}
      <SurfaceContextProvider value={mode}>
        <Scope className={DS_SCOPE_CLASS}>{children}</Scope>
      </SurfaceContextProvider>
    </CssVarsProvider>
  );
}
